"use strict"

import { parseDate } from "./shared_components/utils"
import { logToServer } from "./ErrorBoundary"

const UrlUtils = {
  createGetUrl: (url, params) => {
    let list_params_enc = []
    for (let key in params) {
      const val = params[key]

      if (Array.isArray(val)) {
        val.forEach((elem) => {
          list_params_enc.push(`${key}[]=${encodeURIComponent(elem)}`)
        })
      } else {
        list_params_enc.push(`${key}=${encodeURIComponent(val)}`)
      }
    }

    return url + "?" + list_params_enc.join("&")
  },
}

/* No JSX in this file, but using .jsx extension because it gives better syntax
 * highlighting for modern ES6 in most editors */

const REDIRECT_STATUS_KEY = "lanzacofee-attempted-authentication-redirect"
let RUNNING_FORBIDDEN_RESOURCE_CHECK = false

async function lcFetch(url, opts) {
  const response = await fetch(url, opts)

  if (response.status === 200) {
    localStorage.removeItem(REDIRECT_STATUS_KEY)
  } else if (response.status === 401) {
    if (!RUNNING_FORBIDDEN_RESOURCE_CHECK) {
      if (!localStorage[REDIRECT_STATUS_KEY]) {
        // Consider doing a reload in order to re-authenticate the user.
        // However, it is possible that the 'forbidden' response only
        // applies to a specific resource, so make an extra check to an
        // endpoint which should always be open...

        RUNNING_FORBIDDEN_RESOURCE_CHECK = true
        fetch("/warehouse/runs/", {
          credentials: "same-origin",
          headers: new Headers({
            Accept: "application/json",
          }),
        }).then((resp2) => {
          RUNNING_FORBIDDEN_RESOURCE_CHECK = false
          if (resp2.status === 401) {
            localStorage[REDIRECT_STATUS_KEY] = true
            location.reload()
          }
        })
      } else {
        console.error("Giving up because redirect has already been tried")
      }
    }
  }

  return response
}

// General function to create instance from passed object
export function addNewObject(url, data, detailed_error = false, json_response = true, jsonify_data = true) {
  const final_data = jsonify_data ? JSON.stringify(data) : data
  const extra_headers = jsonify_data ? { "Content-Type": "application/json" } : {}
  return lcFetch(url, {
    credentials: "same-origin",
    method: "POST",
    headers: new Headers({
      ...extra_headers,
      "X-CSRFToken": window.lanzacofee_csrf_token,
    }),
    body: final_data,
  }).then((resp) => {
    if (!resp.ok) {
      if (detailed_error) {
        return Promise.reject(resp)
      } else {
        return Promise.reject(`Couldn't add new object (${url}): ${resp.statusText}`)
      }
    } else {
      if (json_response) {
        return resp.json()
      } else {
        return resp
      }
    }
  })
}

// General function to update instance from passed object
export function updateObject(url, data, detailed_error = false, json_response = true, jsonify_data = true) {
  const final_data = jsonify_data ? JSON.stringify(data) : data
  const extra_headers = jsonify_data ? { "Content-Type": "application/json" } : {}
  return lcFetch(url, {
    credentials: "same-origin",
    method: "PUT",
    headers: new Headers({
      ...extra_headers,
      "X-CSRFToken": window.lanzacofee_csrf_token,
    }),
    body: final_data,
  }).then((resp) => {
    if (!resp.ok) {
      if (detailed_error) {
        return Promise.reject(resp)
      } else {
        return Promise.reject(`Couldn't update object (${url}): ${resp.statusText}`)
      }
    } else {
      if (json_response) {
        return resp.json()
      } else {
        return resp
      }
    }
  })
}

// General function to delete instance from passed object
export function deleteObject(url, detailed_error = false, json_response = true) {
  return lcFetch(url, {
    credentials: "same-origin",
    method: "DELETE",
    headers: new Headers({
      "Content-Type": "application/json",
      "X-CSRFToken": window.lanzacofee_csrf_token,
    }),
  }).then((resp) => {
    if (!resp.ok) {
      if (detailed_error) {
        return Promise.reject(resp)
      } else {
        return Promise.reject(`Couldn't delete object (${url}): ${resp.statusText}`)
      }
    } else {
      if (json_response) {
        return resp.json()
      } else {
        return resp
      }
    }
  })
}

// General function to return object(s) of a certain kind
export function getObjectsList(url, detailed_error = false, json_response = true) {
  return lcFetch(url, {
    credentials: "same-origin",
    headers: new Headers({
      Accept: "application/json",
    }),
  }).then((resp) => {
    if (!resp.ok) {
      if (detailed_error) {
        return Promise.reject(resp)
      } else {
        return Promise.reject(`Couldn't fetch data (${url}): ${resp.statusText}`)
      }
    } else {
      if (json_response) {
        return resp.json()
      } else {
        return resp
      }
    }
  })
}

// General function to return OPTIONS object(s) of a certain kind
export function getOptionsList(url, detailed_error = false, json_response = true) {
  return lcFetch(url, {
    credentials: "same-origin",
    method: "OPTIONS",
    headers: new Headers({
      Accept: "application/json",
    }),
  }).then((resp) => {
    if (!resp.ok) {
      if (detailed_error) {
        return Promise.reject(resp)
      } else {
        return Promise.reject(`Couldn't fetch data (${url}): ${resp.statusText}`)
      }
    } else {
      if (json_response) {
        return resp.json()
      } else {
        return resp
      }
    }
  })
}

export function normalizeRunDetails(runs) {
  const final = []

  runs.forEach((run) => {
    if (Object.keys(run).includes("observationTypes")) {
      const observationTypesFlat = {}
      for (const observationType of Object.keys(run["observationTypes"])) {
        run["observationTypes"][observationType].forEach((tag) => {
          if (Object.keys(observationTypesFlat).includes(tag.tag)) {
            observationTypesFlat[tag.tag] = {
              ...observationTypesFlat[tag.tag],
              types: [...observationTypesFlat[tag.tag]["types"], observationType],
            }
          } else {
            observationTypesFlat[tag.tag] = {
              ...tag,
              types: [observationType],
              tag_name: tag.tag,
              tag_label: tag.label,
            }
          }
        })
      }
      run["observationTypesFlat"] = observationTypesFlat
    }

    const { has_excel, run_parents, biocatalyst_parents, external_runs, external_samples, ...rest } = run

    final.push({
      ...rest,
      actions:
        run.actions &&
        run.actions.map((action) => ({
          ...action,
          timestamp: action.timestamp && parseDate(action.timestamp),
        })),
      started_on: run.started_on && parseDate(run.started_on),
      inoculated_on: run.inoculated_on && parseDate(run.inoculated_on),
      finished_on: run.finished_on && parseDate(run.finished_on),
      observation_max: run.observation_max && parseDate(run.observation_max),
      observation_min: run.observation_max && parseDate(run.observation_min),
      observation_max_day: run.observation_max_day,
      observation_min_day: run.observation_min_day,
      hasExcel: has_excel,
      runParents: run_parents,
      bioCatalystParents: biocatalyst_parents,
      externalRuns: external_runs,
      externalSamples: external_samples,
    })
  })

  return final
}

export function loadRuns(method, data = null, detailed_error = false) {
  const url = "/warehouse/runs/"
  const headers = {}

  if (method === "POST") {
    headers["X-CSRFToken"] = window.lanzacofee_csrf_token
    headers["Content-Type"] = "application/json"
  } else if (method === "GET") {
    headers["Accept"] = "application/json"
  }

  const options = {
    credentials: "same-origin",
    method: method,
    headers: new Headers({ ...headers }),
  }

  if (method === "POST") {
    options["body"] = JSON.stringify(data)
  }

  return lcFetch(url, { ...options })
    .then((response) => {
      if (!response.ok) {
        if (detailed_error) {
          return Promise.reject(response)
        } else {
          return Promise.reject(`Couldn't fetch run data: ${response.statusText}`)
        }
      } else {
        return response.json()
      }
    })
    .then((runs) => {
      if (["POST", "PATCH"].includes(method)) {
        return
      } else if (method === "GET") {
        return normalizeRunDetails(runs.rows)
      }
    })
}

export function loadRunDetails(runID, cutoff) {
  let url = `/warehouse/runs/${runID}/`
  if (cutoff !== undefined) {
    url = url + `?cutoff=${cutoff}`
  }

  return lcFetch(url, {
    credentials: "same-origin",
    headers: new Headers({
      Accept: "application/json",
    }),
  })
    .then((response) => {
      if (!response.ok) {
        return Promise.reject(`Couldn't fetch data: ${response.statusText}`)
      } else {
        return response.json()
      }
    })
    .then((run) => {
      return normalizeRunDetails([run])[0]
    })
}

function processObservationDataSeries(seriesData) {
  const processedData = seriesData.map((obv) => ({
    ...obv,
    day: obv.day && parseFloat(obv.day),
    timestamp: obv.timestamp && parseDate(obv.timestamp),
  }))

  processedData.sort((a, b) => a.timestamp.valueOf() - b.timestamp.valueOf())

  return processedData
}

export function loadObservationSeries(id, tag, units, smp_src, cutoff) {
  // smp_src used/passed on the LL side only
  let url = `/warehouse/runs/${id}/observations/?tag=${encodeURIComponent(tag)}`
  if (units) {
    url = url + `&units=${units}`
  }

  if (cutoff !== undefined) {
    url = url + `&cutoff=${cutoff}`
  }

  return lcFetch(url, {
    credentials: "same-origin",
    headers: new Headers({
      Accept: "application/json",
    }),
  })
    .then((resp) => {
      if (!resp.ok) {
        return Promise.reject(`Couldn't fetch data: ${resp.statusText}`)
      } else {
        return resp.json()
      }
    })
    .then((obsData) => {
      const result = {}
      for (const tag of Object.keys(obsData)) {
        result[tag] = processObservationDataSeries(obsData[tag])
      }
      return result
    })
}

export function indirectUploadFile(data, file_type, process_immediately = false, params = null) {
  let url = `/lake/files/indirect_upload/?file_type=${file_type}`
  process_immediately ? (url = url + "&process_immediately=yes") : null

  if (params) {
    for (let key in params) {
      const val = params[key]
      url += `&${key}=${val}`
    }
  }

  return lcFetch(url, {
    credentials: "same-origin",
    method: "POST",
    headers: new Headers({
      "X-CSRFToken": window.lanzacofee_csrf_token,
    }),
    body: data,
  }).then((resp) => {
    if (!resp.ok) {
      return Promise.reject(`Couldn't upload file entries: ${resp.statusText}`)
    } else {
      return resp.json()
    }
  })
}

export function postFavouriteGraphSet(name, graphConfigs, description, view, interfaces) {
  return lcFetch("/users/favourites/graphlayouts/", {
    credentials: "same-origin",
    method: "POST",
    headers: new Headers({
      "Content-Type": "application/json",
      "X-CSRFToken": window.lanzacofee_csrf_token,
    }),
    body: JSON.stringify({
      name,
      description,
      encoded: JSON.stringify(graphConfigs),
      view,
      interfaces,
    }),
  }).then((resp) => {
    if (!resp.ok) {
      return Promise.reject(`Couldn't upload favourite graph-set: ${resp.statusText}`)
    } else {
      return resp.json()
    }
  })
}

export function patchFavouriteSet(action, id, body, fave_type) {
  const url = `/users/favourites/${fave_type === "graph" ? "graphlayouts" : "runreactors"}/${id}/`
  return lcFetch(url, {
    credentials: "same-origin",
    method: "PATCH",
    headers: new Headers({
      "Content-Type": "application/json",
      "X-CSRFToken": window.lanzacofee_csrf_token,
    }),
    body: JSON.stringify(body),
  }).then((resp) => {
    if (!resp.ok) {
      return Promise.reject(`Couldn't ${action} favourite ${fave_type}-set: ${resp.statusText}`)
    } else {
      return resp.json()
    }
  })
}

// Function to trigger processing of Excel traveler - No graphs.
export const getExcelTravelerFile = (params, intl) => {
  const url = UrlUtils.createGetUrl("/warehouse/traveller-api/getExcelTraveler/", params)
  return lcFetch(url, {
    credentials: "same-origin",
    method: "GET",
  }).then((resp) => {
    if (!resp.ok) {
      return Promise.reject(`There was a problem submitting this data for processing: ${resp.statusText}`)
    } else {
      intl &&
        alert(
          intl.formatMessage({
            id: "general.request.submitted",
            defaultMessage: "Your request has been submitted",
          }),
        )
      return window.open("/user-files/", "_blank")
    }
  })
}

// Function to trigger download of Excel traveler - Graphs and customization
export const getReactorExcelTemplate = (params) => {
  const url = UrlUtils.createGetUrl("/warehouse/traveller-api/getExcelTemplate/", params)
  window.open(url, "_blank")
}

export const getServerlessToken = async (url, username, password) => {
  let token = null

  const tokenQuery = `mutation TokenAuth($username: String!, $password: String!) {
      tokenAuth(username: $username, password: $password) {
        token
        payload
        refreshExpiresIn
      }
    }`

  const requestBody = JSON.stringify({
    query: tokenQuery,
    variables: {
      username: username,
      password: password,
    },
  })

  const headers = { "Content-Type": "application/json" }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: requestBody,
    })

    const respJson = await response.json()
    token = respJson.data.tokenAuth.token

    if (respJson.errors != null) {
      const errMsg = `Errors: ${respJson.errors}`
      console.error(errMsg)
      logToServer(errMsg)
    }
  } catch (err) {
    const errMsg = `Error in getting serverless token:  ${err}`
    console.error(errMsg)
    logToServer(errMsg)
  }

  return token
}

export const getServerlessObjects = async (query, token, url, username, password) => {
  let result = null

  const requestBody = JSON.stringify({
    query: query,
    variables: {
      username: username,
      password: password,
    },
  })

  const headers =
    token == null
      ? { "Content-Type": "application/json" }
      : {
          "Content-Type": "application/json",
          Authorization: `JWT ${token}`,
        }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: requestBody,
    })

    const respJson = await response.json()

    result = respJson.data

    if (respJson.errors != null) {
      const errMsg = `Errors in getting serverless parent objects: ${respJson.errors}`
      console.error(errMsg)
      logToServer(errMsg)
      return respJson
    }
  } catch (err) {
    const errMsg = `Errors in getting serverless parent objects: ${err}`
    console.error(errMsg)
    logToServer(errMsg)
  }

  return result
}
